/**
 * Trigger a browser download for a remote or data-URL image.
 * Uses fetch→blob so cross-origin Firebase Storage URLs still download
 * (plain <a download> is ignored for many cross-origin URLs).
 *
 * @param {string} url
 * @param {string} [filename]
 * @returns {Promise<void>}
 */
export async function downloadImageUrl(url, filename = 'image.png') {
  const src = typeof url === 'string' ? url.trim() : '';
  if (!src) {
    throw new Error('No image URL to download.');
  }
  const safeName =
    typeof filename === 'string' && filename.trim()
      ? filename.trim().replace(/[<>:"/\\|?*\u0000-\u001f]+/g, '_')
      : 'image.png';

  if (src.startsWith('data:') || src.startsWith('blob:')) {
    const link = document.createElement('a');
    link.href = src;
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  const response = await fetch(src, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status}).`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Prefer a sensible filename from a gallery/manifest asset shape.
 * @param {{ assetName?: string, id?: string, manifestAssetId?: string, previewUrl?: string, imageUrl?: string }|null|undefined} asset
 * @param {string} [fallback]
 * @returns {string}
 */
export function filenameForGalleryAsset(asset, fallback = 'image.png') {
  const base =
    (typeof asset?.assetName === 'string' && asset.assetName.trim()) ||
    (typeof asset?.manifestAssetId === 'string' &&
      asset.manifestAssetId.trim()) ||
    (typeof asset?.id === 'string' && asset.id.trim()) ||
    'image';
  const url =
    (typeof asset?.previewUrl === 'string' && asset.previewUrl) ||
    (typeof asset?.imageUrl === 'string' && asset.imageUrl) ||
    '';
  const extMatch = /\.([a-z0-9]{2,5})(?:\?|$)/i.exec(url);
  const ext = extMatch ? extMatch[1].toLowerCase() : 'png';
  const stem = base.replace(/\.[a-z0-9]{2,5}$/i, '');
  return `${stem}.${ext}` || fallback;
}
